import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AmazonFinanceService } from '../../services/amazon-finance.service';
import { FinanceExecutionReport, FinanceExecutionOutcomePage, FinanceExceptionResetResult } from '../../services/amazon-finance.models';

@Component({
  selector: 'gung-amazon-finance-reports',
  templateUrl: './amazon-finance-reports.component.html',
  styleUrl: './amazon-finance-reports.component.scss',
  standalone: false
})
export class AmazonFinanceReportsComponent implements OnInit, OnDestroy {
  reports: FinanceExecutionReport[] = [];
  selected: FinanceExecutionReport | null = null;
  outcomes: FinanceExecutionOutcomePage | null = null;
  page = 0;
  outcomePage = 0;
  status = '';
  resetStatus = 'EXCEPTION';
  loading = false;
  detailLoading = false;
  error = '';
  backlogPreview: FinanceExceptionResetResult | null = null;
  backlogResult: FinanceExceptionResetResult | null = null;
  resetPreview: FinanceExceptionResetResult | null = null;
  resetResult: FinanceExceptionResetResult | null = null;
  recoveryBusy = false;
  mappingSubmitted = false;
  private recoveryRequest = 0;
  private detailRequest = 0;
  private outcomeRequest = 0;
  private readonly destroyed = new Subject<void>();
  constructor(private readonly service: AmazonFinanceService) {}
  ngOnInit(): void {
    this.load();
  }
  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
  load(page = this.page): void {
    if (this.loading || this.recoveryBusy) return;
    this.loading = true;
    this.error = '';
    this.service
      .getExecutionReports(page)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: reports => {
          this.reports = reports;
          this.page = page;
          this.loading = false;
        },
        error: () => {
          this.error = 'REPORTS_LOAD_ERROR';
          this.loading = false;
        }
      });
    if (this.selected) this.open(this.selected.id);
  }
  open(id: string): void {
    if (this.recoveryBusy) return;
    ++this.recoveryRequest;
    this.resetPreview = null;
    this.resetResult = null;
    this.mappingSubmitted = false;
    const request = ++this.detailRequest;
    ++this.outcomeRequest;
    this.selected = null;
    this.outcomes = null;
    this.status = '';
    this.error = '';
    this.detailLoading = true;
    this.service
      .getExecutionReport(id)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: report => {
          if (request !== this.detailRequest) return;
          this.selected = report;
          this.resetStatus = report.afterCounts?.['EXCEPTION'] ? 'EXCEPTION' : 'MAPPED_MISSING_MAPPINGS';
          this.detailLoading = false;
          if (report.status !== 'RUNNING') this.loadOutcomes(0);
        },
        error: () => {
          if (request !== this.detailRequest) return;
          this.error = 'REPORTS_LOAD_ERROR';
          this.detailLoading = false;
        }
      });
  }
  loadOutcomes(page = 0): void {
    if (!this.selected) return;
    const request = ++this.outcomeRequest;
    this.error = '';
    this.outcomes = null;
    this.service
      .getExecutionOutcomes(this.selected.id, page, this.status)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: outcomes => {
          if (request === this.outcomeRequest) {
            this.outcomes = outcomes;
            this.outcomePage = page;
          }
        },
        error: () => {
          if (request === this.outcomeRequest) this.error = 'REPORTS_LOAD_ERROR';
        }
      });
  }
  resetSelectionChanged(): void {
    this.resetPreview = null;
    this.resetResult = null;
    this.mappingSubmitted = false;
  }

  previewReset(): void {
    if (!this.selected || this.recoveryBusy) return;
    const id = this.selected.id;
    const request = ++this.recoveryRequest;
    this.resetPreview = null;
    this.resetResult = null;
    this.error = '';
    this.recoveryBusy = true;
    this.service.previewMappingReset(id, this.resetStatus).pipe(takeUntil(this.destroyed)).subscribe({
      next: result => {
        if (request !== this.recoveryRequest) return;
        this.resetPreview = result;
        this.recoveryBusy = false;
      },
      error: error => this.recoveryError(error)
    });
  }

  resetMappings(): void {
    if (!this.selected || !this.resetPreview?.eligible || this.recoveryBusy) return;
    this.recoveryBusy = true;
    this.error = '';
    this.service.resetReportMappings(this.selected.id, this.resetStatus).pipe(takeUntil(this.destroyed)).subscribe({
      next: result => {
        this.resetResult = result;
        this.resetPreview = null;
        this.mappingSubmitted = false;
        this.recoveryBusy = false;
      },
      error: error => {
        this.resetPreview = null;
        this.recoveryError(error);
      }
    });
  }

  previewBacklog(): void {
    if (this.recoveryBusy) return;
    this.recoveryBusy = true;
    this.error = '';
    this.backlogPreview = null;
    this.backlogResult = null;
    this.service.previewMissingMappingsBacklog().pipe(takeUntil(this.destroyed)).subscribe({
      next: result => {
        this.backlogPreview = result;
        this.recoveryBusy = false;
      },
      error: error => this.recoveryError(error)
    });
  }

  resetBacklog(): void {
    if (!this.backlogPreview?.eligible || this.recoveryBusy) return;
    this.recoveryBusy = true;
    this.error = '';
    this.service.resetMissingMappingsBacklog().pipe(takeUntil(this.destroyed)).subscribe({
      next: result => {
        this.backlogResult = result;
        this.backlogPreview = null;
        this.mappingSubmitted = false;
        this.recoveryBusy = false;
      },
      error: error => {
        this.backlogPreview = null;
        this.recoveryError(error);
      }
    });
  }

  runMapping(): void {
    if (!(this.resetResult?.reset || this.backlogResult?.reset) || this.recoveryBusy || this.mappingSubmitted) return;
    this.recoveryBusy = true;
    this.error = '';
    this.service.runJob('MAP').pipe(takeUntil(this.destroyed)).subscribe({
      next: () => {
        this.mappingSubmitted = true;
        this.recoveryBusy = false;
      },
      error: error => this.recoveryError(error)
    });
  }

  private recoveryError(error: any): void {
    this.recoveryBusy = false;
    this.error = error?.error?.detail || error?.error?.message || 'REPORT_RESET_ERROR';
  }

  duration(ms: number): string {
    const seconds = Math.floor(Math.max(0, ms) / 1000);
    return seconds >= 3600
      ? `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
      : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  }
}
